import { getProductImage } from "../../../utils/productUtils";
import { ImageFormat } from "../../../dto/ProductDataDTO";
import { Card } from "../../../Configurators/Linear/components/Card";
import ProductDesignation from "../ProductDesignation";
import classNames from "classnames";
import CenterContents from "../../../common/components/CenterContents";
import { styled } from "@mui/material";
import { type RelatedProductData } from "./RelatedProductsBlock";
import Parameter from "../../../common/components/Parameter";
import { trackPdpRelatedProductsClick } from "../../../feature/Analytics/ga/ga";
import { IconSize } from "../../../common/components/icon/IconSize";
import { SchaefflerIdParameter } from "../../../common/components/Product/ProductParams";
import { fakeDescriptorFromProduct } from "../../../feature/ProductDescriptor/v1/productDescriptorUtils";
import { type ProductData } from "../../../models/ProductData";
import { Translation } from "../../../common/Translation";
import { useTranslation } from "react-i18next";
import { appendPurchaseParams } from "~/ProductDetails/components/ProductSummary/PdpSummaryHelper";
import { usePurchasePlace } from "~/feature/Analytics/ga/recommendations/RecommendationContexts";
import type { PurchaseType } from "~/feature/Analytics/ga/recommendations/types";

interface RelatedProductProps {
  mainProductDesignation: string;
  product: RelatedProductData;
}

export const MobileRelatedProductCard = ({ product, mainProductDesignation }: RelatedProductProps) => {
  const reco_place = usePurchasePlace();
  const reco_type: PurchaseType = "rp";
  let onClick = undefined;
  let href = undefined;
  if (product.url) {
    onClick = () => trackPdpRelatedProductsClick(mainProductDesignation, product.designation);
    href = appendPurchaseParams(product.url, reco_place, reco_type);
  }

  const image = getProductImage(product, ImageFormat.thumbnail);

  return (
    <Card
      className={"scha__related-product__card"}
      disableHover={!product.url}
      as={product.url ? "a" : "div"}
      href={href}
      onClick={onClick}
    >
      <AspectRatioImage src={image?.url} />
      <CardContent>
        <ProductDesignation
          className={"scha__related-product__card__designation"}
          isPreferred={product.preferred}
          designation={product.designation}
          size={"title-medium"}
          iconSize={IconSize.FONT_16}
        />
      </CardContent>
    </Card>
  );
};

export const DesktopRelatedProductCard = ({ product, mainProductDesignation }: RelatedProductProps) => {
  const reco_place = usePurchasePlace();
  const reco_type: PurchaseType = "rp";
  let onClick = undefined;
  let href = undefined;
  if (product.url) {
    onClick = () => trackPdpRelatedProductsClick(mainProductDesignation, product.designation);
    href = appendPurchaseParams(product.url, reco_place, reco_type);
  }

  const image = getProductImage(product, ImageFormat.thumbnail);
  const { t } = useTranslation();

  // TODO: Send packageAliases via PDP and fix this
  const customerMaterialNumber = (product.aliases || []).join(", ");
  return (
    <Card
      className={"scha__related-product__card"}
      disableHover={!product.url}
      as={product.url ? "a" : "div"}
      href={href}
      onClick={onClick}
    >
      <div className={"scha__related-product__card__image"}>{image && <img src={image.url} alt="" />}</div>
      <div>
        <ProductDesignation
          className={"scha__related-product__card__designation"}
          isPreferred={product.preferred}
          designation={product.designation}
          size={"title-medium"}
          iconSize={IconSize.FONT_16}
        />
        <p className={"scha__related-product__card__description"}>
          <Translation {...product.shortDescription} />
        </p>
        <SchaefflerIdParameter productDescriptor={fakeDescriptorFromProduct(product as ProductData)} />
        {customerMaterialNumber && (
          <Parameter className={"mt-2"} label={t("addProduct.customerId")} value={customerMaterialNumber} />
        )}
      </div>
    </Card>
  );
};

interface AspectRatioImageProps {
  className?: string;
  src?: string;
  alt?: string;
}

/**
 * Image with a 16:9 ratio. In case src is not provided, it will still occupy space
 */
const AspectRatioImage = ({ className, src, alt }: AspectRatioImageProps) => {
  return (
    <div className={classNames("forced-ratio-container--9-16", className)}>
      <CenterImage>{src && <img src={src} alt={alt} />}</CenterImage>
    </div>
  );
};

const CenterImage = styled(CenterContents)({
  padding: "8px",
});

const CardContent = styled("div")({
  padding: "12px",
});
